import { useState } from 'react';

import ReduceCapacityIcon from '@mui/icons-material/ReduceCapacity';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, Button, Typography } from '@mui/material';
import { Button as ActionButton, HttpError, useNotify, useUpdate } from 'react-admin';
import { DataProviderResource } from 'src/constants';
import { LinkAccountBodyDto } from 'src/dtos';
import { HttpErrorTypography } from 'src/lib';
import { BaseAccount } from 'src/models';

import { LinkAdditionalAccountList } from './LinkAdditionalAccountList';

interface LinkAdditionalAccountActionButtonProps {
  rootAccount: BaseAccount;
  accounts?: BaseAccount[];
}

export const LinkAdditionalAccountActionButton = ({
  rootAccount,
  accounts,
}: LinkAdditionalAccountActionButtonProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const notify = useNotify();
  const [linkAccount, { isError, error, isLoading, reset }] = useUpdate<
    Partial<LinkAccountBodyDto> & { id: string },
    HttpError
  >();
  const [selectedAccount, setSelectedAccount] = useState<BaseAccount>();

  const onLink = () => {
    linkAccount(
      DataProviderResource.LinkAccount,
      {
        id: rootAccount.id,
        data: {
          id: selectedAccount?.id,
          primaryAccountHolder: {
            ssid: selectedAccount?.primaryAccountHolder?.ssid,
            dateOfBirth: selectedAccount?.primaryAccountHolder?.dateOfBirth,
          },
        },
      },
      {
        mutationMode: 'pessimistic',
        onSuccess: () => {
          notify(`Additional account was successfully linked.`, {
            type: 'success',
            undoable: true,
          });
          _onClose();
        },
        onSettled: () => {
          // refresh();
        },
      },
    );
  };

  const resetModal = () => {
    reset();
  };

  const _onClose = () => {
    setIsOpen(false);
    resetModal();
    setSelectedAccount(undefined);
  };

  return (
    <>
      <ActionButton
        label='Link Additional Account'
        onClick={() => {
          setIsOpen(true);
        }}>
        <ReduceCapacityIcon />
      </ActionButton>
      <Dialog open={isOpen} onClose={() => setIsOpen(false)} fullScreen>
        <DialogTitle>
          <Grid container direction='column' spacing={2}>
            <Grid item>
              <Typography variant='h5'> Link Additional Account</Typography>
            </Grid>
            <Grid item>
              <Typography variant='body1'>
                Select an account from the list to link to the user primary account (
                {rootAccount.primaryAccountHolder?.email}, {rootAccount.accountNumber}, {rootAccount.customTypeLabel}).
              </Typography>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <LinkAdditionalAccountList
            value={selectedAccount}
            rootAccountId={rootAccount.id}
            accounts={accounts}
            onChange={value => {
              setSelectedAccount(value);
            }}
          />
          <Grid container direction='column' spacing={2}>
            <Grid item md={12} sm={12}>
              {isError && <HttpErrorTypography error={error} />}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid item xs={12} container direction='row' justifyContent='flex-end' spacing={2}>
            <Grid item>
              <Button variant='outlined' color='inherit' size='medium' onClick={_onClose}>
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant='contained'
                color='primary'
                size='medium'
                disabled={isLoading || !selectedAccount}
                type='submit'
                onClick={onLink}>
                Link
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </>
  );
};
